import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

export type CameraState = 'idle' | 'requesting' | 'active' | 'denied' | 'unsupported' | 'error';

/**
 * getUserMedia hook for the rear camera.
 *
 * Returns a **callback ref** to attach to a `<video>` element plus the current
 * state. The stream is attached automatically whenever both the stream and the
 * video element are available — even when the element mounts *after* the
 * stream resolves (e.g. the AR view renders only after permission is granted).
 */
export function useCamera() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [state, setState] = useState<CameraState>('idle');
  const [error, setError] = useState<string | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  /** Attach the current stream to the video element if both exist. */
  const attachStream = useCallback(() => {
    if (videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      void videoRef.current.play().catch(() => {});
    }
  }, []);

  /**
   * Callback ref — React calls this with the DOM node when the `<video>`
   * element mounts (and with `null` on unmount). If a stream was already
   * acquired we attach it immediately so the feed is never lost to a race
   * between `getUserMedia` resolving and the element entering the tree.
   */
  const videoCallbackRef = useCallback(
    (node: HTMLVideoElement | null) => {
      videoRef.current = node;
      if (node && streamRef.current) {
        node.srcObject = streamRef.current;
        void node.play().catch(() => {});
      }
    },
    []
  );

  // Re-attach when state transitions to 'active' (covers the case where the
  // video element is already mounted when the stream resolves).
  useEffect(() => {
    if (state === 'active') {
      attachStream();
    }
  }, [state, attachStream]);

  const start = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setState('unsupported');
      setError('Camera not supported on this device');
      return;
    }

    setState('requesting');
    setError(null);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false
      });
      streamRef.current = stream;
      // Attach immediately if the video element is already mounted; otherwise
      // the callback ref (on mount) or the state effect will handle it.
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setState('active');
    } catch (err) {
      if (err instanceof DOMException && (err.name === 'NotAllowedError' || err.name === 'SecurityError')) {
        setState('denied');
        setError('Camera permission denied');
      } else {
        setState('error');
        setError(err instanceof Error ? err.message : 'Failed to access camera');
      }
    }
  }, []);

  const stop = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setState('idle');
  }, []);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  // Stable identity — callers must not re-create callback refs every render
  // (that detaches <video> and blanks the camera feed).
  return useMemo(
    () => ({ videoRef: videoCallbackRef, state, error, start, stop }),
    [videoCallbackRef, state, error, start, stop],
  );
}
